import type { Dispatch as ToolkitDispatch } from '@reduxjs/toolkit';
import { useEffect, useRef } from 'react';
import { useAppSelector } from 'store/hooks';
import type { CustomServerError } from 'store/services/depositAPI';
import {
  appChannelSelector,
  isInitLoadingSelector,
  isSessionExpiredSelector,
  setSessionExpired,
} from 'store/slices/global';
import { getFirstConsistentlyInteractive } from 'tti-polyfill';
import { addPwPrefixToBaseUrl } from 'utils/pwContext';
import { useGetSessionId } from 'utils/session';
import { onCLS, onLCP } from 'web-vitals';

import { fromNumberToStringForFloat, getWidgetLocation, parseQuery, WIDGET_API } from '../utils';

enum locationEnum {
  mainPage = 'MainPage',
  widgetForm = 'WidgetForm',
}

type Metric = {
  name: string;
  value?: string;
};

type RequestMetricsData = {
  metrics: Metric[];
  tags: Record<string, string>;
};

interface PrepareDataForRequest {
  metricName: string;
  value?: number | string;
  tags?: Record<string, string>;
}

const hasValue = (value?: number | string): value is number | string => value !== undefined && value !== null;

const prepareDataForRequest = (props: PrepareDataForRequest): RequestMetricsData => {
  const { metricName, value, tags = {} } = props;
  const requestData: RequestMetricsData = {
    metrics: [],
    tags,
  };

  const hasMetricsValue = hasValue(value);

  if (metricName === 'TTI' && hasMetricsValue) {
    const ttiMetric = {
      name: 'TTI',
      value: `${Math.round(+value)}`,
    };

    requestData.metrics = [...requestData.metrics, ttiMetric];
  }

  if (metricName === 'CLS' && hasMetricsValue) {
    const CLSMetric = {
      name: 'CLS',
      value: fromNumberToStringForFloat(value),
    };

    requestData.metrics = [...requestData.metrics, CLSMetric];
  }

  if (metricName === 'LCP' && hasMetricsValue) {
    const LCPMetric = {
      name: 'LCP',
      value: `${Math.round(+value)}`,
    };

    requestData.metrics = [...requestData.metrics, LCPMetric];
  }

  if (metricName === 'init_call_duration' && hasMetricsValue) {
    const ttiMetric = {
      name: 'init_call_duration',
      value: `${Math.round(+value)}`,
    };

    requestData.metrics = [...requestData.metrics, ttiMetric];
  }

  return requestData;
};

const postMetric = async (requestUrl: string, sessionId: string, xChannel: string, requestData: RequestMetricsData) => {
  if (!sessionId || requestData.metrics.length === 0) return;

  const body = JSON.stringify(requestData);
  await fetch(requestUrl, {
    method: 'POST',
    mode: 'cors',
    cache: 'default',
    headers: {
      'X-Session-Id': sessionId,
      'content-type': 'application/json',
    },
    body,
    keepalive: true,
  });
};

export const useMetricsRequest = (dispatch: ToolkitDispatch) => {
  const { search, pathname } = window.location;
  const params = parseQuery(search);
  const widgetLocation = getWidgetLocation(pathname);
  const { metrics, xChannel } = params;
  const sessionId = useGetSessionId();
  const isSessionExpired = useAppSelector(isSessionExpiredSelector);
  const isInitLoading = useAppSelector(isInitLoadingSelector);
  const metricsState = useRef({
    hasTTI: false,
    hasCLS: false,
    hasLCP: false,
    isSessionExpired: false,
    CLSMetricValue: 0,
    LCPMetricValue: 0,
  });

  const trackTTIMetric = (requestUrl: string) => {
    if (!metricsState.current.hasTTI && !isInitLoading) {
      getFirstConsistentlyInteractive().then((tti) => {
        if (metricsState.current.isSessionExpired) return;

        if (tti) {
          const requestData = prepareDataForRequest({
            metricName: 'TTI',
            value: tti,
          });
          postMetric(requestUrl, sessionId, `${xChannel}`, requestData)
            .then(() => {
              metricsState.current.hasTTI = true;
            })
            .catch((e) => {
              console.error(e);
              if (e && (e as CustomServerError)?.errorCode === 17) {
                metricsState.current.isSessionExpired = true;
                dispatch(setSessionExpired());
              }
            });
        }
      });
    }
  };

  const trackOnPageMetrics = (requestUrl: string) => {
    if (!metricsState.current.hasCLS && !isInitLoading) {
      onCLS(
        (metric) => {
          try {
            if (metricsState.current.isSessionExpired) return;
            if (!metric.entries || metric.entries.length === 0) return;

            const { value } = metric.entries[0];

            if (value && metricsState.current.CLSMetricValue !== value) {
              metricsState.current.CLSMetricValue = value;
              const page = widgetLocation.isWidgetForm ? locationEnum.widgetForm : locationEnum.mainPage;
              const requestData = prepareDataForRequest({
                metricName: 'CLS',
                value,
                tags: {
                  page,
                },
              });

              postMetric(requestUrl, sessionId, `${xChannel}`, requestData)
                .then(() => {
                  metricsState.current.hasCLS = true;
                })
                .catch((e) => {
                  console.error(e);
                  if (e && (e as CustomServerError)?.errorCode === 17) {
                    metricsState.current.isSessionExpired = true;
                    dispatch(setSessionExpired());
                  }
                });
            }
          } catch (e) {
            console.error(e);
            if (e && (e as CustomServerError)?.errorCode === 17) {
              metricsState.current.isSessionExpired = true;
              dispatch(setSessionExpired());
            }
          }
        },
        {
          reportAllChanges: true,
        },
      );
    }
    if (!metricsState.current.hasLCP && !isInitLoading) {
      onLCP((metric) => {
        try {
          if (metricsState.current.isSessionExpired) return;

          const { value } = metric;

          if (value && metricsState.current.LCPMetricValue !== value) {
            metricsState.current.LCPMetricValue = value;
            const page = widgetLocation.isWidgetForm ? locationEnum.widgetForm : locationEnum.mainPage;
            const requestData = prepareDataForRequest({
              metricName: 'LCP',
              value,
              tags: {
                page,
              },
            });
            postMetric(requestUrl, sessionId, `${xChannel}`, requestData)
              .then(() => {
                metricsState.current.hasLCP = true;
              })
              .catch((e) => {
                console.error(e);
                if (e && (e as CustomServerError)?.errorCode === 17) {
                  metricsState.current.isSessionExpired = true;
                  dispatch(setSessionExpired());
                }
              });
          }
        } catch (e) {
          console.error(e);
          if (e && (e as CustomServerError)?.errorCode === 17) {
            metricsState.current.isSessionExpired = true;
            dispatch(setSessionExpired());
          }
        }
      });
    }
  };

  const fetchMetricsData = (trackCallback: (p: string) => void) => {
    const flowType =
      pathname.includes('withdrawal') && !pathname.includes('reject-withdrawal') ? 'payouts' : 'payments';

    const baseMetricsUrl = `${WIDGET_API}/${flowType}/metrics`;
    const requestUrl = addPwPrefixToBaseUrl(baseMetricsUrl);

    trackCallback(requestUrl);
  };

  useEffect(() => {
    if (!metrics || metrics === '0' || !sessionId || isSessionExpired || isInitLoading) {
      return;
    }

    fetchMetricsData(trackTTIMetric);
  }, [isInitLoading]);

  useEffect(() => {
    if (!metrics || metrics === '0' || !sessionId || isSessionExpired || isInitLoading) {
      return;
    }

    fetchMetricsData(trackOnPageMetrics);
  }, [
    // NOTE: uncomment and remove hasCLS & hasLCP - to track metric after redirect (change page).
    // key,
    isInitLoading,
  ]);

  useEffect(() => {
    metricsState.current.isSessionExpired = isSessionExpired;
  }, [isSessionExpired]);
};

interface UseInitTrackingParams {
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
  metricUrl?: string;
  metricName?: string;
  dispatch: ToolkitDispatch;
}

export const useInitTracking = ({
  isLoading,
  isFetching,
  isSuccess,
  startedTimeStamp,
  fulfilledTimeStamp,
  metricUrl,
  metricName,
  dispatch,
}: UseInitTrackingParams) => {
  const sessionId = useGetSessionId();
  const appChannel = useAppSelector(appChannelSelector);

  const startTimeRef = useRef<number | null>(null);
  const trackingRef = useRef({
    sent: false,
    isSessionExpired: false,
  });

  useEffect(() => {
    if (!metricUrl || !metricName) return;
    if (trackingRef.current.isSessionExpired) return;

    if ((isLoading || isFetching) && startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }

    if (isSuccess && startTimeRef.current !== null && !trackingRef.current.sent) {
      const duration =
        startedTimeStamp && fulfilledTimeStamp
          ? fulfilledTimeStamp - startedTimeStamp
          : Date.now() - startTimeRef.current;

      const requestData = prepareDataForRequest({
        metricName,
        value: duration,
      });

      postMetric(metricUrl, sessionId, appChannel, requestData)
        .then(() => {
          trackingRef.current.sent = true;
        })
        .catch((e) => {
          if (e?.errorCode === 17) {
            trackingRef.current.isSessionExpired = true;
            dispatch(setSessionExpired());
          }
        });
    }
  }, [
    isLoading,
    isFetching,
    isSuccess,
    metricUrl,
    metricName,
    sessionId,
    appChannel,
    dispatch,
    startedTimeStamp,
    fulfilledTimeStamp,
  ]);
};
